import { Box, Text, VStack } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import FlexSearch from "flexsearch";
import { memo, useCallback } from "react";
import { SearchTableContent } from "./tables/search-table-content";
import { TableRow } from "./utils/tables-metadata";

/** Pre-indexed and filtered table data passed from parent */
export type FilteredTableData = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  index: FlexSearch.Index;
  indexingPromises: Promise<FlexSearch.Index>[];
  filteredRows: TableRow[];
  isEmptySection: boolean;
};

export type SelectedRowState = {
  row: TableRow;
  tableType: string;
  suspectsContext?: string;
};

interface SearchResultsContentProps {
  fhirSdk: FhirBundleSdk;
  children: React.ReactNode;
  filterQuery: string;
  filteredTableData: FilteredTableData[];
  onRowSelect: (rowState: SelectedRowState) => void;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
}

/**
 * Search mode content component.
 * Receives pre-indexed and pre-filtered data from parent.
 * Labs and Lab Panels are rendered as separate searchable sections (no toggle).
 * Featured section is NOT shown in search mode - users want to see search results only.
 */
function SearchResultsContentComponent({
  fhirSdk,
  children,
  filterQuery,
  filteredTableData,
  onRowSelect,
  onPdfView,
  isRowViewed,
  onDocumentDownloaded,
}: SearchResultsContentProps) {
  /**
   * Handles row click from any table.
   * Notifies parent to handle side panel opening and selection.
   */
  const handleRowClick = useCallback(
    (row: TableRow, tableType: string, suspectsContext?: string) => {
      onRowSelect({ row, tableType, suspectsContext });
    },
    [onRowSelect]
  );

  const allSectionsEmpty = filteredTableData.every(table => table.isEmptySection);

  return (
    <Box backgroundColor="bg.canvas">
      <VStack align="stretch">
        <Box id="ai-summary">{children}</Box>
        <VStack align="stretch">
          {allSectionsEmpty && (
            <Box
              py={10}
              px={8}
              borderRadius="lg"
              backgroundColor="bg.subtle"
              border="1px"
              borderColor="border"
              textAlign="center"
            >
              <Text fontWeight="semibold" color="fg.muted" fontSize="lg">
                No results found
              </Text>
              <Text color="fg.subtle" fontSize="sm" mt={2}>
                Try different keywords, filters, or review your spelling.
              </Text>
            </Box>
          )}

          {filteredTableData.map(({ tableType, displayName, filteredRows, isEmptySection }) => {
            if (isEmptySection) {
              return null;
            }

            return (
              <Box
                key={tableType}
                id={tableType}
                w="100%"
                mb={2}
                borderRadius="lg"
                bg="bg.surface"
                px={6}
                py={6}
                border="1px"
                borderColor="border"
                boxShadow="sm"
              >
                <SearchTableContent
                  tableType={tableType}
                  displayName={displayName}
                  filteredRows={filteredRows}
                  fhirSdk={fhirSdk}
                  filterQuery={filterQuery}
                  handleRowClick={handleRowClick}
                  handlePdfView={onPdfView}
                  isRowViewed={isRowViewed}
                  onDocumentDownloaded={onDocumentDownloaded}
                />
              </Box>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
}

export const SearchResultsContent = memo(SearchResultsContentComponent);
